import { escapeHtml } from '../format';
import type { IntakeSession, IntakeStatusUpdate } from '../intake-client';
import { EXPORT_GUIDES, PC_PARTIAL_NOTE } from './copy';
import { requireElement, type MountedScreen } from './dom';
import { mountEmailCard, type EmailCard } from './email-card';
import type { UiEnvironment } from './environment';
import { hasDraggedFiles, watchFileInput } from './file-drop';
import { icon } from './icons';
import { bindInAppNotice, inAppNoticeMarkup } from './inapp-notice';
import type { IntakeApi } from './intake-api';
import type { PollerVisibility } from './intake-poller';
import type { Toast } from './toast';

/**
 * Start screen (spec §6.2-1): file card, e-mail card, sample, export guides.
 * Phones see the e-mail card first; computers see the file card first.
 */

export const FILE_ACCEPT = '.txt,.zip,.csv,text/plain,text/csv,application/zip,application/x-zip-compressed';

export interface StartScreenOptions {
  readonly environment: UiEnvironment;
  readonly intake: IntakeApi;
  readonly toast: Toast;
  readonly onFiles: (files: File[]) => void;
  readonly onIntakeReceived: (session: IntakeSession, status: IntakeStatusUpdate) => void;
  readonly onSample: () => void;
  readonly visibility?: PollerVisibility;
}

function fileCardMarkup(featured: boolean): string {
  return `
    <article class="krc-card krc-choice krc-file-card${featured ? ' is-featured' : ''}" aria-labelledby="krc-file-title">
      <div class="krc-card-head">
        <span class="krc-card-icon">${icon('file')}</span>
        <span class="krc-badge${featured ? '' : ' is-quiet'}">${featured ? 'PC 추천' : 'PC·파일'}</span>
      </div>
      <h3 id="krc-file-title">파일로 올리기</h3>
      <p class="krc-card-text">카톡에서 내보낸 대화 파일(.txt · .zip · .csv)을 골라 주세요. 나눠 저장된 파일도 한 번에 고를 수 있어요.</p>
      <label class="krc-dropzone" id="krc-dropzone" for="krc-file-input">
        <span class="krc-dropzone-icon">${icon('upload')}</span>
        <span class="krc-dropzone-copy"><strong>대화 파일 고르기</strong><small>또는 여기로 끌어다 놓기</small></span>
        <input id="krc-file-input" type="file" multiple accept="${FILE_ACCEPT}" aria-describedby="krc-file-note" />
      </label>
      <p class="krc-card-note" id="krc-file-note">파일은 서버로 보내지 않고 이 브라우저에서만 읽어요. 최대 200MB.</p>
    </article>`;
}

function emailCardShell(featured: boolean): string {
  return `<article class="krc-card krc-choice krc-email-card${featured ? ' is-featured' : ''}" id="krc-email-card" aria-labelledby="krc-email-title"></article>`;
}

function guidesMarkup(): string {
  return `
    <section class="krc-guides" aria-labelledby="krc-guides-title">
      <h3 id="krc-guides-title">대화 내보내는 방법</h3>
      ${EXPORT_GUIDES.map((guide) => `
        <details class="krc-guide" data-guide="${guide.id}">
          <summary>${escapeHtml(guide.title)}</summary>
          <ol>${guide.steps.map((step) => `<li>${escapeHtml(step)}</li>`).join('')}</ol>
          ${guide.note ? `<p>${escapeHtml(guide.note)}</p>` : ''}
        </details>`).join('')}
      <p class="krc-guides-note">${icon('alert')}<span>${escapeHtml(PC_PARTIAL_NOTE)}</span></p>
    </section>`;
}

export function mountStartScreen(stage: HTMLElement, options: StartScreenOptions): MountedScreen {
  const { environment } = options;
  const emailFirst = environment.mobile || environment.kakaoInApp;
  const cards = emailFirst
    ? [emailCardShell(true), fileCardMarkup(false)]
    : [fileCardMarkup(true), emailCardShell(false)];

  const section = document.createElement('section');
  section.className = 'krc-screen krc-start';
  section.setAttribute('aria-labelledby', 'krc-start-title');
  section.innerHTML = `
    ${inAppNoticeMarkup(environment, 'start')}
    <h2 class="krc-screen-title" id="krc-start-title" tabindex="-1">어떻게 올릴까요?</h2>
    <div class="krc-choices">${cards.join('')}</div>
    <div class="krc-sample">
      <p><strong>파일이 없어도 괜찮아요.</strong> 가상의 대화로 성적표가 어떻게 나오는지 먼저 볼 수 있어요.</p>
      <button class="button button-soft krc-sample-button" type="button" id="krc-sample">${icon('sparkle')}<span>샘플로 보기</span></button>
    </div>
    ${guidesMarkup()}`;
  stage.append(section);
  bindInAppNotice(section, environment, options.toast);

  const input = requireElement<HTMLInputElement>(section, '#krc-file-input');
  const dropzone = requireElement<HTMLElement>(section, '#krc-dropzone');
  const email: EmailCard = mountEmailCard(requireElement(section, '#krc-email-card'), {
    api: options.intake,
    platform: environment.phonePlatform,
    featured: emailFirst,
    toast: options.toast,
    onReceived: options.onIntakeReceived,
    visibility: options.visibility,
  });

  const takeFiles = (list: FileList | null | undefined) => {
    const files = list ? Array.from(list) : [];
    if (files.length > 0) options.onFiles(files);
  };

  // Stops forwarding picks on dispose: an open file dialog outlives this screen.
  const stopWatchingInput = watchFileInput(input, options.onFiles);
  requireElement<HTMLButtonElement>(section, '#krc-sample').addEventListener('click', () => options.onSample());

  // Drops anywhere on the page land here while this screen is shown. On other
  // screens the app-level guard (guardFileDrops) keeps the tab from opening them.
  let dragDepth = 0;
  const onDragEnter = (event: DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepth += 1;
    dropzone.classList.add('is-dragging');
  };
  const onDragOver = (event: DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (event: DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) dropzone.classList.remove('is-dragging');
  };
  const onDrop = (event: DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepth = 0;
    dropzone.classList.remove('is-dragging');
    takeFiles(event.dataTransfer?.files);
  };
  document.addEventListener('dragenter', onDragEnter);
  document.addEventListener('dragover', onDragOver);
  document.addEventListener('dragleave', onDragLeave);
  document.addEventListener('drop', onDrop);

  return {
    heading: requireElement(section, '#krc-start-title'),
    dispose() {
      email.dispose();
      stopWatchingInput();
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
      section.remove();
    },
  };
}
